import { EllipsisVerticalIcon, TrashIcon } from '@heroicons/react/24/outline';
import { LinearProgress } from '@mui/joy';
import {
  OnChangeFn,
  PaginationState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { DataGridSearchInput } from '@/components/DataGrid/DataGridSearchInput';
import { useOrganizationId } from '@/hooks/useSwr';
import ThumbsUpIcon from '@/icons/ThumbsUpIcon.svg?react';
import { ComparativeExperimentSchema } from '@/types/schema';
import { getDatasetPath } from '@/utils';
import { addQueryParams } from '@/utils/add-query-params';
import {
  apiComparativeExperimentsPath,
  apiDatasetsPath,
  appComparePath,
  appPublicDatasetsPath,
  appPublicPath,
} from '@/utils/constants';
import { getPrettyFeedbackName } from '@/utils/get-pretty-feedback-name';
import { cn } from '@/utils/tailwind';
import { useLocalStorageState } from '@/utils/use-local-storage-state';

import { DataGrid, DataGridPagination } from '../DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  useDataGridSizingLocalStorage,
} from '../DataGrid.utils';
import { DeleteModal } from '../Delete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../Dropdown';
import { TableCellTextOverflow } from '../Table';

const PAIRWISE_LOCAL_STORAGE_COLUMN_VISIBILITY =
  'ls:pairwise-experiments-table-column-visibility';
const CELL_SPACING_VARIANT = 'tight';

const columnHelper = createColumnHelper<ComparativeExperimentSchema>();

export const ComparativeExperimentsDataGrid = (props: {
  comparativeExperiments: ComparativeExperimentSchema[];
  datasetId?: string;
  datasetShareToken?: string;
  pagination: PaginationState;
  setPagination: OnChangeFn<PaginationState>;
  sortingState: SortingState;
  handleSortingChange: OnChangeFn<SortingState>;
  total: number;
  name: string;
  setName: (value: string) => void;
  emptyState: React.ReactNode;
  isLoading: boolean;
}) => {
  'use no memo';
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const [comparativeExperimentsToDelete, setComparativeExperimentsToDelete] =
    useState<string[]>([]);
  const allPairwiseFeedbacksArray = props.comparativeExperiments.reduce<
    string[]
  >((acc, curr) => {
    return acc.concat(
      curr.feedback_stats ? Object.keys(curr.feedback_stats) : []
    );
  }, []);
  const allPairwiseFeedbacks = Array.from(new Set(allPairwiseFeedbacksArray));
  const columns = [
    columnHelper.accessor('name', {
      id: 'name',
      meta: { name: 'Name' },
      header: () => <div className="pl-3">Name</div>,
      enableSorting: false,
      cell: ({ getValue }) => (
        <TableCellTextOverflow spacingVariant={CELL_SPACING_VARIANT}>
          <div className="overflow-auto pl-3 text-sm font-normal no-scrollbar">
            {getValue()}
          </div>
        </TableCellTextOverflow>
      ),
    }),
    columnHelper.accessor('experiments_info', {
      id: 'pair',
      meta: { name: 'Experiment Pair' },
      header: () => <div className="pl-3">Experiment Pair</div>,
      enableSorting: false,
      cell: ({ row }) => {
        const { firstSession, secondSession } = getFirstAndSecondSession(
          row.original
        );
        return (
          <TableCellTextOverflow spacingVariant={CELL_SPACING_VARIANT}>
            <div className="mr-5 overflow-auto pl-3 text-sm font-normal no-scrollbar">
              {firstSession?.name} <span className="text-tertiary">vs</span>{' '}
              {secondSession?.name}
            </div>
          </TableCellTextOverflow>
        );
      },
    }),
    ...allPairwiseFeedbacks.map((feedback) => {
      return columnHelper.accessor(feedback as any, {
        id: feedback,
        meta: { name: getPrettyFeedbackName(feedback) },
        header: () => (
          <div className="min-w-[150px]  self-center text-center">
            {getPrettyFeedbackName(feedback)}
          </div>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const { firstSession, secondSession } = getFirstAndSecondSession(
            row.original
          );
          if (!firstSession || !secondSession) {
            return null;
          }
          const firstSessionWinners =
            row.original.feedback_stats?.[feedback]?.[firstSession.id];
          const secondSessionWinners =
            row.original.feedback_stats?.[feedback]?.[secondSession.id];
          if (
            firstSessionWinners === undefined ||
            secondSessionWinners === undefined
          ) {
            return null;
          }
          const winningSession =
            firstSessionWinners > secondSessionWinners
              ? firstSession.id
              : secondSessionWinners > firstSessionWinners
              ? secondSession.id
              : null;

          const isFirstSessionWinning = winningSession === firstSession.id;
          const isSecondSessionWinning = winningSession === secondSession.id;
          const isTie = winningSession === null;

          return (
            <div className="flex w-full items-center justify-center gap-2 self-center">
              <div
                className={cn('flex items-center justify-center gap-3 py-1')}
              >
                <div className="flex w-[36px] min-w-[36px] max-w-[36px] shrink-0 justify-end">
                  {isFirstSessionWinning && (
                    <div className="rounded-full bg-success-secondary p-1">
                      <ThumbsUpIcon className="h-4 w-4 text-success" />
                    </div>
                  )}
                  {isTie && (
                    <span className="rounded-full bg-brand-primary px-2 text-brand-primary">
                      Tie
                    </span>
                  )}
                </div>
                <span
                  className={cn(
                    'w-[25px] text-center',
                    isFirstSessionWinning && 'font-extrabold'
                  )}
                >
                  {firstSessionWinners}
                </span>
              </div>
              <div className="h-[20px] w-[2px] shrink-0 bg-tertiary" />
              <div className={cn('flex items-center gap-3 py-1')}>
                <span
                  className={cn(
                    'w-[20px] text-center',
                    isSecondSessionWinning && 'font-extrabold'
                  )}
                >
                  {secondSessionWinners}
                </span>
                <div className="flex w-[36px] min-w-[36px] max-w-[36px] justify-start">
                  {isSecondSessionWinning && (
                    <div className="rounded-full bg-success-secondary p-1">
                      <ThumbsUpIcon className="h-4 w-4 text-success" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        },
      });
    }),
    columnHelper.accessor('created_at', {
      id: 'created_at',
      header: 'Created at',
      enableResizing: false,
      enableSorting: true,
      maxSize: 150,
      cell: ({ getValue }) => {
        return (
          <TableCellTextOverflow spacingVariant={CELL_SPACING_VARIANT}>
            <div className="overflow-auto text-sm font-normal no-scrollbar">
              {new Date(getValue()).toLocaleString()}
            </div>
          </TableCellTextOverflow>
        );
      },
    }),
    ...(props.datasetShareToken
      ? []
      : [
          columnHelper.display({
            id: 'actions',
            cell: ({ row, table }) => {
              const { actions } = (table.options.meta ?? {}) as {
                actions?: {
                  handleDelete?: (id: string) => void;
                };
              };

              return (
                <TableCellTextOverflow className="justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none"
                      data-testid="dropdown-menu-trigger"
                    >
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownMenuItem
                        onClick={() => actions?.handleDelete?.(row.original.id)}
                        className="flex items-center gap-2"
                      >
                        <TrashIcon className="h-5 w-5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCellTextOverflow>
              );
            },
            enableSorting: false,
            enableHiding: false,
            enableResizing: false,
            size: 40,
          }),
        ]),
  ];

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('comparative');

  const onClick = (row: ComparativeExperimentSchema, event) => {
    let targetUrl = '';
    const queryParamsToAdd = {
      selectedSessions: row.experiments_info.map((e) => e.id).join(','),
      comparativeExperiment: row.id,
    };
    if (props.datasetShareToken) {
      targetUrl = addQueryParams(
        `/${appPublicPath}/${props.datasetShareToken}/${appPublicDatasetsPath}/${appComparePath}`,
        queryParamsToAdd
      );
    } else {
      const basePath = getDatasetPath(
        props.datasetId ?? '',
        organizationId ?? ''
      );
      targetUrl = addQueryParams(
        `${basePath}/${appComparePath}`,
        queryParamsToAdd
      );
    }
    if (!emulateNativeClick(targetUrl, event.nativeEvent)) {
      navigate(targetUrl);
    }
  };

  const onMiddleClick = (row: ComparativeExperimentSchema) => {
    let targetUrl = '';
    const queryParamsToAdd = {
      selectedSessions: row.experiments_info.map((e) => e.id).join(','),
      comparativeExperiment: row.id,
    };
    if (props.datasetShareToken) {
      targetUrl = addQueryParams(
        `/${appPublicPath}/${props.datasetShareToken}/${appPublicDatasetsPath}/${appComparePath}`,
        queryParamsToAdd
      );
    } else {
      const basePath = getDatasetPath(
        props.datasetId ?? '',
        organizationId ?? ''
      );
      targetUrl = addQueryParams(
        `${basePath}/${appComparePath}`,
        queryParamsToAdd
      );
    }

    if (!emulateNativeMiddleClick(targetUrl)) {
      navigate(targetUrl);
    }
  };

  const [columnVisibilityFromLocalStorage] = useLocalStorageState(
    PAIRWISE_LOCAL_STORAGE_COLUMN_VISIBILITY,
    { name: false }
  );

  const table = useReactTable({
    columns,
    data: props.comparativeExperiments,
    state: {
      pagination: props.pagination,
      sorting: props.sortingState,
      columnSizing,
    },
    initialState: {
      columnVisibility: columnVisibilityFromLocalStorage,
    },
    onPaginationChange: props.setPagination,
    onColumnSizingChange: setColumnSizing,

    meta: {
      actions: {
        handleDelete: (toDelete: string) =>
          setComparativeExperimentsToDelete([toDelete]),
      },
    },

    getRowId: (row) => row.id,

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),

    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: false,
    enableSorting: true,
    onSortingChange: props.handleSortingChange,
    enableSortingRemoval: false,

    columnResizeMode: 'onChange',
    pageCount: Math.ceil(+(props.total || 0) / props.pagination.pageSize),
  });

  return props.comparativeExperiments.length > 0 ? (
    <div className="flex flex-col gap-4">
      <div className="flex gap-3 self-end">
        <DataGridSearchInput value={props.name} onChange={props.setName} />
        <DataGridColumnVisibilityPopover
          table={table}
          localStorageKey={PAIRWISE_LOCAL_STORAGE_COLUMN_VISIBILITY}
        />
      </div>
      <div className="overflow-hidden rounded-lg border border-secondary">
        <DataGrid
          table={table}
          cellHeight={56}
          cellSpacingVariant={CELL_SPACING_VARIANT}
          isLoading={props.isLoading}
          emptyState={props.emptyState}
          onClick={onClick}
          onMiddleClick={onMiddleClick}
        />
      </div>
      <DataGridPagination table={table} />
      <DeleteModal
        endpoint={apiComparativeExperimentsPath}
        id={comparativeExperimentsToDelete[0]}
        isOpen={!!comparativeExperimentsToDelete[0]}
        doClose={() => setComparativeExperimentsToDelete([])}
        invalidationPrefixes={[apiDatasetsPath]}
      />
    </div>
  ) : props.isLoading ? (
    <LinearProgress />
  ) : (
    <div>{props.emptyState}</div>
  );
};

const getFirstAndSecondSession = (row: ComparativeExperimentSchema) => {
  return {
    firstSession: row.experiments_info[0],
    secondSession: row.experiments_info[1],
  };
};
